class ComparisonTable extends HTMLElement {
  constructor() {
    super();
    this.variantMetafields = {};
    this.boundHandleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.loadVariantData();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  // Load variant data from JSON script
  loadVariantData() {
    const dataScript = document.getElementById('variant-data');
    const dataScriptDebug = document.getElementById('variant-data-debug');

    if (dataScript) {
      try {
        this.variantMetafields = JSON.parse(dataScript.textContent);
      } catch (error) {
        console.error('Error parsing variant data:', error);
      }
    } else {
      console.error('No variant-data script found');
    }

    // Merge debug data if available
    if (dataScriptDebug) {
      try {
        const debugData = JSON.parse(dataScriptDebug.textContent);
        // Merge debug data into main variant data
        Object.keys(debugData).forEach(variantId => {
          if (this.variantMetafields[variantId]) {
            this.variantMetafields[variantId] = {
              ...this.variantMetafields[variantId],
              ...debugData[variantId]
            };
          } else {
            this.variantMetafields[variantId] = debugData[variantId];
          }
        });
        // console.log('Debug variant data merged successfully');
      } catch (error) {
        console.error('Error parsing debug variant data:', error);
      }
    }
  }

  // Update table values
  updateTable(variantId) {
    const metafields = this.variantMetafields[variantId];

    if (metafields) {
      // Update each field within this element
      Object.keys(metafields).forEach(field => {
        // Handle variant_image_columnId fields
        if (field.startsWith('variant_image_')) {
          const columnId = field.replace('variant_image_', '');
          const element = this.querySelector(`[data-field="variant_image"][data-column-id="${columnId}"]`);
          if (element) {
            const imgTag = element.querySelector('img');
            if (imgTag && metafields[field]) {
              imgTag.src = metafields[field];
            }
          }
        }
        // Handle value_columnId_rowId fields
        else if (field.startsWith('value_')) {
          const element = this.querySelector(`[data-field="${field}"]`);
          if (element && metafields[field]) {
            element.innerHTML = metafields[field];
          }
        }
        // Handle regular fields (variant_image, variant_title, etc.)
        else {
          const element = this.querySelector(`[data-field="${field}"]`);
          if (element) {
            // Special handling for variant image
            if (field === 'variant_image') {
              const imgTag = element.querySelector('img');
              if (imgTag) {
                imgTag.src = metafields[field];
              }
            } else {
              // For all other fields, update innerHTML
              element.innerHTML = metafields[field];
            }
          }
        }
      });
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('comparison-updated', {
      detail: { variantId, metafields },
      bubbles: true
    }));
  }

  // Handle variant changes
  handleVariantChange(event) {
    let variantId = null;

    // Different ways to get the variant ID
    if (event.detail?.variant?.id) {
      variantId = event.detail.variant.id;
    } else if (event.detail?.variantId) {
      variantId = event.detail.variantId;
    } else if (event.target.value) {
      variantId = event.target.value;
    } else if (event.currentTarget?.querySelector) {
      const checkedInput = event.currentTarget.querySelector('[name="id"]:checked');
      variantId = checkedInput?.value;
    }

    // console.log('🔄 Variant change detected:', variantId);

    if (variantId) {
      // console.log('📊 Available variant data:', Object.keys(this.variantMetafields));
      // console.log('🎯 Data for variant', variantId, ':', this.variantMetafields[variantId]);
      this.updateTable(variantId);
    }
  }

  // Setup event listeners
  setupEventListeners() {
    // Listen for custom Shopify events
    document.addEventListener('variant:change', this.boundHandleVariantChange);

    // Detect Shopify variant selectors
    const variantSelectors = document.querySelectorAll('variant-selects, variant-radios, .variant-input');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', this.boundHandleVariantChange);
    });

    // Listen for product forms
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    productForms.forEach(form => {
      const variantInputs = form.querySelectorAll('input[name="id"], select[name="id"]');
      variantInputs.forEach(input => {
        input.addEventListener('change', this.boundHandleVariantChange);
      });
    });

    // Listen for custom dropdown changes
    this.setupCustomDropdownListeners();

    // Detect initial variant
    this.detectInitialVariant();
  }

  // Remove event listeners
  removeEventListeners() {
    document.removeEventListener('variant:change', this.boundHandleVariantChange);

    const variantSelectors = document.querySelectorAll('variant-selects, variant-radios, .variant-input');
    variantSelectors.forEach(selector => {
      selector.removeEventListener('change', this.boundHandleVariantChange);
    });

    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    productForms.forEach(form => {
      const variantInputs = form.querySelectorAll('input[name="id"], select[name="id"]');
      variantInputs.forEach(input => {
        input.removeEventListener('change', this.boundHandleVariantChange);
      });
    });
  }

  // Public method to update manually
  update(variantId) {
    this.updateTable(variantId);
  }

  // Public method to get variant data
  getVariantData(variantId) {
    return this.variantMetafields[variantId] || null;
  }

  // Setup listeners for custom dropdowns
  setupCustomDropdownListeners() {
    const customDropdowns = document.querySelectorAll('.custom-dropdown');

    customDropdowns.forEach(dropdown => {
      const items = dropdown.querySelectorAll('.custom-dropdown-content li');

      items.forEach(item => {
        item.addEventListener('click', () => {
          const variantId = item.dataset.varid;
          if (variantId) {
            // Small delay to ensure the input updates first
            setTimeout(() => {
              this.updateTable(variantId);
            }, 50);
          }
        });
      });
    });
  }

  // Detect initial variant
  detectInitialVariant() {
    // Search for input with current variant
    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      this.updateTable(variantInput.value);
    }

    // Also search in URL for variant parameter
    const urlParams = new URLSearchParams(window.location.search);
    const variantFromUrl = urlParams.get('variant');
    if (variantFromUrl) {
      this.updateTable(variantFromUrl);
    }
  }

}

// Register the custom element
customElements.define('comparison-table', ComparisonTable);